import React, { useEffect, useState } from 'react'
import style from './style.module.scss'
import PropsPanel from './EditorParts/EditorProps'
import EditorControls from './EditorParts/EditorControls'
import EditorStructure, { Structure } from './EditorParts/EditorStructure'
import { useUpdateContentMutation } from 'src/redux/Api/caseStudiesApi'
import { ICaseStudyContent } from 'src/utils/Interfaces/caseStudies.interface'

export const componentRegistry: { [key: string]: React.ElementType } = {}

export interface DataElement {
  type: string
  props: any
}

interface EditorCoreProps {
  data?: ICaseStudyContent
  type?: 'content' | 'result'
}

interface registerComponentProps {
  name: string
  component: React.ElementType
}

export const registerComponent = (components: registerComponentProps[]) => {
  components.forEach((component) => {
    componentRegistry[component.name] = component.component
  })
}

registerComponent([
  {
    name: 'uiImage',
    component: React.lazy(() => import('./EditorComponents/uiImage'))
  },
  {
    name: 'uiParagraph',
    component: React.lazy(() => import('./EditorComponents/uiParagraph'))
  },
  {
    name: 'uiSubtitle',
    component: React.lazy(() => import('./EditorComponents/uiSubtitle'))
  },
  {
    name: 'uiList',
    component: React.lazy(() => import('./EditorComponents/uiList'))
  },
  {
    name: 'uiCompare',
    component: React.lazy(() => import('./EditorComponents/uiCompare'))
  },
  {
    name: 'uiIncrease',
    component: React.lazy(() => import('./EditorComponents/uiIncrease'))
  },
  {
    name: 'uiMetric',
    component: React.lazy(() => import('./EditorComponents/uiMetric'))
  },
  {
    name: 'uiNote',
    component: React.lazy(() => import('./EditorComponents/uiNote'))
  }
])

const EditorCore = ({ data, type }: EditorCoreProps) => {
  const [updateContent] = useUpdateContentMutation()
  const [fullScreen, setFullScreen] = useState(false)
  const [content, setContent] = useState<DataElement[]>(data?.content || [])
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null)
  const updateProps = (newProps: any) => {
    if (selectedIndex === null) return
    const newContent = [...content]
    newContent[selectedIndex] = { ...newContent[selectedIndex], props: newProps }
    setContent(newContent)
  }
  useEffect(() => {
    if (content.length === 0) {
      setSelectedIndex(null)
    }
  }, [content])
  const handleSave = () => {
    if (type === 'content') {
      updateContent({
        id: data?.id,
        content: content
      })
    } else {
      updateContent({
        id: data?.id,
        result: content
      })
    }
  }

  const updateStructure = (data: Structure) => {
    const newContent = data.map((element) => {
      const index = content.findIndex((item) => item.props.id === element.id)
      return content[index]
    })
    setContent(newContent)
  }

  return (
    <div className={style.editor + ' ' + (fullScreen ? style.fullscreen : '')}>
      <div className={style.editor_wrapper}>
        <EditorControls onUpdate={setContent} />
        <div className={style.contet_wrapper}>
          <div className={style.content_structure}>
            <p>Structure</p>
            <EditorStructure
              content={content}
              onUpdate={(data) => updateStructure(data)}
              updateSelected={setSelectedIndex}
              selectedIndex={selectedIndex}
            />
          </div>
          <div className={style.content_editable}>
            {content.map((item, index) => {
              const Component = componentRegistry[item.type]
              if (!Component) return <div key={index}>Undefined component {item.type}</div>

              return (
                <div onClick={() => setSelectedIndex(index)} key={index}>
                  <Component {...item.props} />
                </div>
              )
            })}
          </div>

          {/* Панель управления */}
          <div className={style.content_params}>
            <p>Params</p>
            {selectedIndex !== null && content.length !== 0 && content[selectedIndex] && (
              <PropsPanel
                selectedProps={content[selectedIndex]?.props}
                onUpdate={updateProps}
                type={content[selectedIndex].type}
              />
            )}
          </div>
        </div>
      </div>
      <div className={style.buttons}>
        <button className="btn" onClick={() => setFullScreen(!fullScreen)}>
          {fullScreen ? 'Exit Fullscreen' : 'Fullscreen'}
        </button>
        <button className="btn green" onClick={handleSave}>
          Save
        </button>
      </div>
    </div>
  )
}

export default EditorCore
